import React, { useState } from "react";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import axios from "axios";
import { changePassword } from "../../../api/bseApi";

/**
 * BSE password validation rules (UI level)
 * - Min length (BSE policy - typically 8)
 * - Uppercase letter
 * - Number
 * - Special character
 */
const MIN_LENGTH = 8;
const SPECIAL_CHARS = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/;

function validateNewPassword(pwd) {
  const errors = [];
  if (!pwd || pwd.length < MIN_LENGTH) {
    errors.push(`Minimum ${MIN_LENGTH} characters required`);
  }
  if (!/[A-Z]/.test(pwd)) {
    errors.push("At least one uppercase letter required");
  }
  if (!/[0-9]/.test(pwd)) {
    errors.push("At least one number required");
  }
  if (!SPECIAL_CHARS.test(pwd)) {
    errors.push("At least one special character required (!@#$%^&* etc.)");
  }
  return errors;
}

/**
 * ChangePasswordScreen - BSE StAR MF Change Password
 * Opens from Settings / Profile / Security section
 */
export default function ChangePasswordScreen() {
  const [form, setForm] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};

    if (!form.oldPassword || !form.oldPassword.trim()) {
      newErrors.oldPassword = "Old password is required";
    }
    const pwdErrors = validateNewPassword(form.newPassword);
    if (pwdErrors.length > 0) {
      newErrors.newPassword = pwdErrors.join(". ");
    }
    if (form.newPassword !== form.confirmPassword) {
      newErrors.confirmPassword = "New password and confirm password must match";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      const res = await changePassword({
        oldPassword: form.oldPassword,
        newPassword: form.newPassword,
      });

      if (res.data?.status === 1) {
        setForm({ oldPassword: "", newPassword: "", confirmPassword: "" });
        Swal.fire({
          icon: "success",
          title: "Password changed successfully",
          text: "You will be redirected to login. Please sign in with your new password.",
          confirmButtonText: "OK",
          customClass: { popup: "custom-swal-popup" },
        }).then(() => {
          localStorage.removeItem(process.env.REACT_APP_LOGIN_DATA_STORE);
          localStorage.removeItem("authToken");
          axios.post(`${process.env.REACT_APP_BACKEND_BASE_URL || "http://localhost:8000/api"}/user/logout`).catch(() => {});
          window.location.href = "https://gpsfinvest.investwell.app/app/#/login";
        });
      } else {
        const msg = res.data?.bseMessage || res.data?.message || "Change password failed";
        setErrors({ submit: msg });
      }
    } catch (err) {
      const msg = err.response?.data?.bseMessage || err.response?.data?.message || err.message || "Change password failed";
      setErrors({ submit: msg });
    } finally {
      setLoading(false);
      setForm({ oldPassword: "", newPassword: "", confirmPassword: "" });
    }
  };

  return (
    <div className="main-content">
      <div className="page-wrapper page-settings">
        <div className="content">
          <div
            className="mb-4"
            style={{
              background: "#ffffff",
              borderRadius: "8px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              padding: "20px",
            }}
          >
            <nav aria-label="breadcrumb" style={{ marginBottom: "15px" }}>
              <ol className="breadcrumb">
                <li className="breadcrumb-item"><Link to="/dashboard">Dashboard</Link></li>
                <li className="breadcrumb-item"><Link to="/admin/security/roles-permissions">Security</Link></li>
                <li className="breadcrumb-item active">Change BSE Password</li>
              </ol>
            </nav>
            <h5>Change BSE StAR MF Password</h5>
            <p className="text-muted mb-0">
              Change the BSE StAR MF login password. After success, you will be logged out and must sign in again with the new password.
            </p>
          </div>

          <div
            style={{
              background: "#ffffff",
              borderRadius: "8px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              padding: "24px",
              border: "1px solid #e6eef0",
              maxWidth: "480px",
            }}
          >
            <h6 className="mb-4" style={{ color: "#0b6f68", fontWeight: "600" }}>
              Change Password
            </h6>

            {errors.submit && (
              <div className="alert alert-danger mb-4">
                <i className="fas fa-exclamation-circle me-2" />
                {errors.submit}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="mb-3">
                <label className="form-label fw-semibold">
                  Old Password <span className="text-danger">*</span>
                </label>
                <input
                  type="password"
                  className={`form-control ${errors.oldPassword ? "is-invalid" : ""}`}
                  name="oldPassword"
                  value={form.oldPassword}
                  onChange={handleChange}
                  placeholder="Enter current BSE password"
                  autoComplete="current-password"
                  disabled={loading}
                />
                {errors.oldPassword && <div className="invalid-feedback">{errors.oldPassword}</div>}
              </div>

              <div className="mb-3">
                <label className="form-label fw-semibold">
                  New Password <span className="text-danger">*</span>
                </label>
                <input
                  type="password"
                  className={`form-control ${errors.newPassword ? "is-invalid" : ""}`}
                  name="newPassword"
                  value={form.newPassword}
                  onChange={handleChange}
                  placeholder="Enter new password"
                  autoComplete="new-password"
                  disabled={loading}
                />
                {errors.newPassword && <div className="invalid-feedback d-block">{errors.newPassword}</div>}
                <div className="form-text small">
                  Min {MIN_LENGTH} chars, uppercase, number, special character
                </div>
              </div>

              <div className="mb-4">
                <label className="form-label fw-semibold">
                  Confirm New Password <span className="text-danger">*</span>
                </label>
                <input
                  type="password"
                  className={`form-control ${errors.confirmPassword ? "is-invalid" : ""}`}
                  name="confirmPassword"
                  value={form.confirmPassword}
                  onChange={handleChange}
                  placeholder="Confirm new password"
                  autoComplete="new-password"
                  disabled={loading}
                />
                {errors.confirmPassword && <div className="invalid-feedback">{errors.confirmPassword}</div>}
              </div>

              <div className="d-flex gap-2">
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" />
                      Changing...
                    </>
                  ) : (
                    "Change Password"
                  )}
                </button>
                <Link to="/admin/security/roles-permissions" className="btn btn-outline-secondary">
                  Cancel
                </Link>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
